import React from "react";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { PublicKey, SystemProgram } from "@solana/web3.js";
import * as anchor from "@coral-xyz/anchor";
import Idl from "../../idl/vesting.json";
import {
  createAssociatedTokenAccountInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import {
  TOKEN_MINT,
  VESTING_CONFIG_ACCOUNT,
  VESTING_PROGRAM_ID,
} from "utils/envs";
import { Buffer } from "buffer";

const PROGRAM_ID = new PublicKey(VESTING_PROGRAM_ID);

export function CreateVesting() {
  const {
    publicKey,
    signTransaction,
    signAllTransactions,
    sendTransaction,
    connected,
  } = useWallet();
  const { connection } = useConnection();
  const [beneficiary, setBeneficiary] = React.useState("");
  const [totalAmount, setTotalAmount] = React.useState("");
  const [tgePercentage, setTgePercentage] = React.useState("");
  const [tgeTime, setTgeTime] = React.useState("");
  const [totalSteps, setTotalSteps] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const tokenMint = TOKEN_MINT;
  const decimals = 9;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publicKey || !connected) return;

    try {
      setLoading(true);

      // Validate inputs
      if (!PublicKey.isOnCurve(beneficiary)) {
        throw new Error("Invalid beneficiary address");
      }
      if (isNaN(Number(totalAmount)) || Number(totalAmount) <= 0) {
        throw new Error("Invalid total amount");
      }
      if (
        isNaN(Number(tgePercentage)) ||
        Number(tgePercentage) < 0 ||
        Number(tgePercentage) > 100
      ) {
        throw new Error("Invalid TGE percentage");
      }
      if (isNaN(Number(tgeTime)) || Number(tgeTime) < 0) {
        throw new Error("Invalid TGE time");
      }
      if (isNaN(Number(totalSteps)) || Number(totalSteps) <= 0) {
        throw new Error("Invalid total steps");
      }
      if (!startDate) {
        throw new Error("Invalid start date");
      }
      if (
        !endDate ||
        new Date(startDate).getTime() >= new Date(endDate).getTime()
      ) {
        throw new Error("Invalid end date");
      }

      // Convert dates to timestamps
      const startTimestamp = Math.floor(new Date(startDate).getTime() / 1000);
      let endTimestamp = Math.floor(new Date(endDate).getTime() / 1000);
      let tgeTimestamp = startTimestamp + Number(tgeTime) * 24 * 60 * 60; // Convert days to seconds

      // DEBUG
      // tgeTimestamp = startTimestamp + (Number(tgeTime) * 24 * 60 * 60) + (1 * 60 * 60) + 10*60; // UTC+7 = UTC + 7h => Set to 08:00 GMT+7 (01:00 UTC)
      // endTimestamp = tgeTimestamp + 10 * 60; // Set to 10 minutes after TGE
      // console.log("start Date:", new Date(tgeTimestamp * 1000).toISOString());
      // console.log("end Date:", new Date(endTimestamp * 1000).toISOString());

      // Create an anchor provider
      const provider = new anchor.AnchorProvider(
        connection,
        {
          publicKey,
          signTransaction: signTransaction!,
          signAllTransactions: signAllTransactions!,
        },
        { commitment: "confirmed" }
      );

      // Create the program instance with the provider
      const program = new anchor.Program(Idl as any, PROGRAM_ID, provider);
      const tx = new anchor.web3.Transaction();

      const [userStatePda] = PublicKey.findProgramAddressSync(
        [Buffer.from("user_state"), new PublicKey(beneficiary).toBuffer()],
        program.programId
      );

      const [configPda] = PublicKey.findProgramAddressSync(
        [Buffer.from("config")],
        program.programId
      );

      let vestingSeq: anchor.BN;

      try {
        const userState = (await program.account.userState.fetch(
          userStatePda
        )) as {
          userVestingSeq: anchor.BN;
        };
        vestingSeq = userState.userVestingSeq;
        console.log("✅ user_state already initialized");
      } catch (e) {
        console.log("🆕 user_state not found, initializing...");

        const initUserStateIx = await program.methods
          .initUserState()
          .accounts({
            admin: publicKey,
            userState: userStatePda,
            beneficiary: new PublicKey(beneficiary),
            config: configPda,
            systemProgram: anchor.web3.SystemProgram.programId,
          })
          .instruction();

        tx.add(initUserStateIx);
        console.log("✅ user_state initialized");
        vestingSeq = new anchor.BN(0);
      }

      console.log("Vesting sequence:", vestingSeq.toNumber());

      const [vestingPda] = PublicKey.findProgramAddressSync(
        [
          Buffer.from("vesting"),
          new PublicKey(beneficiary).toBuffer(),
          new PublicKey(tokenMint).toBuffer(),
          vestingSeq.toArrayLike(Buffer, "le", 8),
        ],
        program.programId
      );

      const vestingTokenAccount = await getAssociatedTokenAddress(
        new PublicKey(tokenMint),
        vestingPda,
        true // allowOwnerOffCurve = true for PDA
      );
      console.log("Vesting Token Account:", vestingTokenAccount.toBase58());

      // Create ATA if it doesn't exist yet
      const ataInfo = await connection.getAccountInfo(vestingTokenAccount);
      if (!ataInfo) {
        const createATAIx = createAssociatedTokenAccountInstruction(
          publicKey, // payer
          vestingTokenAccount, // ATA to create
          vestingPda, // owner of ATA (PDA)
          new PublicKey(tokenMint)
        );

        tx.add(createATAIx);
      }

      const initVestingIx = await program.methods
        .initializeVesting(
          new anchor.BN(totalAmount).mul(
            new anchor.BN(10).pow(new anchor.BN(decimals))
          ),
          new anchor.BN(tgePercentage),
          new anchor.BN(tgeTimestamp),
          new anchor.BN(totalSteps),
          new anchor.BN(startTimestamp),
          new anchor.BN(endTimestamp)
        )
        .accounts({
          beneficiary: new PublicKey(beneficiary),
          mint: new PublicKey(tokenMint),
          vestingTokenAccount,
          vesting: vestingPda,
          admin: publicKey,
          payer: publicKey,
          userState: userStatePda,
          systemProgram: SystemProgram.programId,
          config: new PublicKey(VESTING_CONFIG_ACCOUNT),
          tokenProgram: TOKEN_PROGRAM_ID,
        })
        .instruction();

      tx.add(initVestingIx);
      const signature = await sendTransaction(tx, connection);
      console.log("✅ Vesting created:", signature);

      // Reset form
      setBeneficiary("");
      setTotalAmount("");
      setTgePercentage("");
      setTgeTime("");
      setTotalSteps("");
      setStartDate("");
      setEndDate("");

      console.log("Vesting schedule created successfully!");
    } catch (error: any) {
      console.error("Error creating vesting schedule:", error.stack);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bit-card w-full">
      <h2 className="bit-title mb-4">Create Vesting Schedule</h2>
      <form onSubmit={handleSubmit} className="space-y-4 !text-sm">
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <label className="bit-label">Beneficiary Address</label>
            <input
              type="text"
              value={beneficiary}
              onChange={(e) => setBeneficiary(e.target.value)}
              className="bit-input w-1/2"
              placeholder="Enter beneficiary address"
              disabled={loading}
            />
          </div>
          <div className="flex justify-between items-center">
            <label className="bit-label">Total Amount</label>
            <input
              type="number"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              className="bit-input w-1/2"
              placeholder="Enter total amount"
              disabled={loading}
            />
          </div>
          <div className="flex justify-between items-center">
            <label className="bit-label">TGE Percentage</label>
            <input
              type="number"
              value={tgePercentage}
              onChange={(e) => setTgePercentage(e.target.value)}
              className="bit-input w-1/2"
              placeholder="Enter TGE percentage"
              disabled={loading}
            />
          </div>
          <div className="flex justify-between items-center">
            <label className="bit-label">TGE Time (days)</label>
            <input
              type="number"
              value={tgeTime}
              onChange={(e) => setTgeTime(e.target.value)}
              className="bit-input w-1/2"
              placeholder="Enter TGE time in days"
              disabled={loading}
            />
          </div>
          <div className="flex justify-between items-center">
            <label className="bit-label">Total Steps</label>
            <input
              type="number"
              value={totalSteps}
              onChange={(e) => setTotalSteps(e.target.value)}
              className="bit-input w-1/2"
              placeholder="Enter total steps"
              disabled={loading}
            />
          </div>
          <div className="flex justify-between items-center">
            <label className="bit-label">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bit-input w-1/2"
              disabled={loading}
            />
          </div>
          <div className="flex justify-between items-center">
            <label className="bit-label">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bit-input w-1/2"
              disabled={loading}
            />
          </div>
        </div>
        <div className="flex justify-end mt-8">
          <button type="submit" className="bit-button" disabled={loading}>
            {loading ? "Creating..." : "Create Schedule"}
          </button>
        </div>
      </form>
    </div>
  );
}
